import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Accordion, Card, ButtonGroup } from 'react-bootstrap';
import { shouldSelectExportColumnByDefault } from './utils/enhancedExportColumnConfig';

const getInitialSectionSelections = (filters) => ({
  base: true,
  engineering: filters.departments.engineering,
  fabrication: filters.departments.fabrication,
  controlPanel: filters.departments.controlPanel,
  pumps: filters.departments.pumps,
  assembly: filters.departments.assembly,
  test: filters.departments.test,
  northPrairie: filters.departments.northPrairie,
  shipping: filters.departments.shipping,
  portalDetails: filters.departments.portalDetails,
  details: true,
});

const EnhancedExportModal = ({ show, onHide, onExport, filters, mode = 'export' }) => {
  // Define all possible columns by section, memoized to avoid recreating on every render
  const allColumns = React.useMemo(() => ({
    base: [
      { id: 'jobNumber', name: 'Job Number', required: true },
      { id: 'modelNumber', name: 'Model Number', required: true },
      { id: 'projectName', name: 'Project Name', required: true },
      { id: 'orderDate', name: 'Order Date', required: false },
      { id: 'productionRelease', name: 'Production Release', required: false },
      { id: 'releaseMeetingDate', name: 'Release Meeting Date', required: false }
    ],
    engineering: [
      { id: 'mechanicalEngineer', name: 'Mechanical Engineer', required: false },
      { id: 'electricalEngineer', name: 'Electrical Engineer', required: false },
      { id: 'controlsEngineer', name: 'Controls Engineer', required: false },
      { id: 'mechanicalDue', name: 'Mechanical Due', required: false },
      { id: 'mechanicalDone', name: 'Mechanical Done', required: false },
      { id: 'electricalDue', name: 'Electrical Due', required: false },
      { id: 'electricalDone', name: 'Electrical Done', required: false }
    ],
    fabrication: [
      { id: 'npPipeFabDue', name: 'NP Pipe Fab Due', required: false },
      { id: 'npPipeFabDone', name: 'NP Pipe Fab Done', required: false },
      { id: 'fusionBondRequired', name: 'Fusion Bond Required', required: false },
      { id: 'fabDue', name: 'Fab Due', required: false },
      { id: 'fabDone', name: 'Fab Done', required: false },
      { id: 'fabExpectedCompletion', name: 'Fab Expected Completion', required: false }
    ],
    controlPanel: [
      { id: 'softwareDue', name: 'Software Due', required: false },
      { id: 'softwareDone', name: 'Software Done', required: false },
      { id: 'controlPanelDue', name: 'Control Panel Due', required: false },
      { id: 'controlPanelDone', name: 'Control Panel Done', required: false },
      { id: 'controlPanelExpectedCompletion', name: 'Control Panel Expected Completion', required: false }
    ],
    pumps: [
      { id: 'hcPumpsDue', name: 'HC Pumps Due', required: false },
      { id: 'hcPumpsDone', name: 'HC Pumps Done', required: false },
      { id: 'vtPumpsDue', name: 'VT Pumps Due', required: false },
      { id: 'pumpsDone', name: 'VT Pumps Done', required: false },
      { id: 'pumpExpectedCompletion', name: 'Pump Expected Completion', required: false }
    ],
    assembly: [
      { id: 'assemblyDue', name: 'Assembly Due', required: false },
      { id: 'assemblyDone', name: 'Assembly Done', required: false },
      { id: 'assemblyExpectedCompletion', name: 'Assembly Expected Completion', required: false }
    ],
    test: [
      { id: 'testDue', name: 'Test Due', required: false },
      { id: 'testDone', name: 'Test Done', required: false },
      { id: 'testExpectedCompletion', name: 'Test Expected Completion', required: false }
    ],
    northPrairie: [
      { id: 'npPipeFabDue', name: 'NP Pipe Fab Due', required: false },
      { id: 'npPipeFabDone', name: 'NP Pipe Fab Done', required: false },
      { id: 'npCsmDue', name: 'NP CSM Due', required: false },
      { id: 'npCsmDone', name: 'NP CSM Done', required: false },
      { id: 'npSuctionLineDue', name: 'NP Suction Line Due', required: false },
      { id: 'npSuctionLineDone', name: 'NP Suction Line Done', required: false },
      { id: 'npInducerTubeDue', name: 'NP Inducer Tube Due', required: false },
      { id: 'npInducerTubeDone', name: 'NP Inducer Tube Done', required: false },
      { id: 'npBmxDue', name: 'NP BMX Due', required: false },
      { id: 'npBmxDone', name: 'NP BMX Done', required: false }
    ],
    shipping: [
      { id: 'packDue', name: 'Pack Due', required: false },
      { id: 'transportType', name: 'Transport Type', required: false },
      { id: 'originalScheduledShipDate', name: 'Original Scheduled Ship Date', required: false },
      { id: 'currentPlanShipDate', name: 'Current Plan Ship Date', required: false },
      { id: 'actualShipDate', name: 'Actual Ship Date', required: false },
      { id: 'scheduledArrivalDate', name: 'Scheduled Arrival Date', required: false }
    ],
    portalDetails: [
      { id: 'salesRep', name: 'Sales Rep', required: false },
      { id: 'jobLocation', name: 'Job Location', required: false }
    ],
    details: [
      { id: 'notes', name: 'Notes', required: false },
      { id: 'paymentTerms', name: 'Payment Terms', required: false },
      { id: 'dollarValue', name: 'Dollar Value', required: false },
      { id: 'leadTime', name: 'Lead Time', required: false }
    ]
  }), []);

  // Export format state - include print option
  const [exportFormat, setExportFormat] = useState('excel');
  
  // Initialize section expanded state
  const [expandedSections, setExpandedSections] = useState({});
  // Initialize selected columns state
  const [selectedColumns, setSelectedColumns] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize section selection state (for select all in a section)
  const [selectedSections, setSelectedSections] = useState(
    () => getInitialSectionSelections(filters)
  );

  // Initialize selected columns based on filters and dueDatesOnly mode - only run once on mount
  useEffect(() => {
    if (show && !isInitialized) {
      const initialSelectedColumns = {};
      
      // Required columns are always selected
      Object.keys(allColumns).forEach(section => {
        allColumns[section].forEach(column => {
          const selectedByDefault = shouldSelectExportColumnByDefault({
            column,
            dueDatesOnly: filters.dueDatesOnly,
            sectionSelected: selectedSections[section],
          });
          initialSelectedColumns[column.id] = Boolean(
            initialSelectedColumns[column.id] || selectedByDefault
          );
        });
      });
      
      setSelectedColumns(initialSelectedColumns);
      setIsInitialized(true);
    }
  }, [show, filters.dueDatesOnly, allColumns, selectedSections, isInitialized]);

  // Rebuild export defaults from the current table filters each time the modal opens.
  useEffect(() => {
    if (!show) return;
    setSelectedSections({
      base: true,
      engineering: filters.departments.engineering,
      fabrication: filters.departments.fabrication,
      controlPanel: filters.departments.controlPanel,
      pumps: filters.departments.pumps,
      assembly: filters.departments.assembly,
      test: filters.departments.test,
      northPrairie: filters.departments.northPrairie,
      shipping: filters.departments.shipping,
      portalDetails: filters.departments.portalDetails,
      details: true,
    });
    setIsInitialized(false);
  }, [
    show,
    filters.dueDatesOnly,
    filters.departments.engineering,
    filters.departments.fabrication,
    filters.departments.controlPanel,
    filters.departments.pumps,
    filters.departments.assembly,
    filters.departments.test,
    filters.departments.northPrairie,
    filters.departments.shipping,
    filters.departments.portalDetails,
  ]);

  // Toggle individual column selection
  const handleColumnToggle = (columnId) => {
    setSelectedColumns(prev => {
      const newSelectedColumns = {
        ...prev,
        [columnId]: !prev[columnId]
      };
      
      // Update section states based on column selections
      const newSectionStates = { ...selectedSections };
      Object.keys(allColumns).forEach(section => {
        const visibleColumns = allColumns[section].filter(column => {
          // Handle North Prairie special case - hide NP Pipe columns if Fabrication is selected
          if (section === 'northPrairie') {
            const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
            return !(isNPPipeColumn && selectedSections.fabrication);
          }
          return true;
        });
        
        const selectedInSection = visibleColumns.filter(column => newSelectedColumns[column.id]).length;
        
        // Update section state: section is "on" if any columns are selected
        // Base section is always considered "on" 
        if (section === 'base') {
          newSectionStates[section] = true;
        } else {
          newSectionStates[section] = selectedInSection > 0;
        }
      });
      
      setSelectedSections(newSectionStates);
      return newSelectedColumns;
    });
  };

  // Toggle a whole section
  const handleSectionToggle = (section) => {
    const isCurrentlySelected = selectedSections[section];
    
    setSelectedSections(prev => ({
      ...prev,
      [section]: !isCurrentlySelected
    }));
    
    setSelectedColumns(prev => {
      const updatedColumns = { ...prev };
      
      // Get visible columns for this section
      const visibleColumns = allColumns[section].filter(column => {
        // Handle North Prairie special case - hide NP Pipe columns if Fabrication is selected
        if (section === 'northPrairie') {
          const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
          return !(isNPPipeColumn && selectedSections.fabrication);
        }
        return true;
      });
      
      if (!isCurrentlySelected) {
        // Section is being turned ON - apply the defaults for the current table view.
        visibleColumns.forEach(column => {
          if (!column.required) {
            updatedColumns[column.id] = shouldSelectExportColumnByDefault({
              column,
              dueDatesOnly: filters.dueDatesOnly,
              sectionSelected: true,
            });
          }
        });
      } else {
        // Section is being turned OFF - uncheck all non-required columns in this section
        visibleColumns.forEach(column => {
          if (!column.required) {
            updatedColumns[column.id] = false;
          }
        });
      }
      
      return updatedColumns;
    });
  };

  // Handle section expansion toggle
  const handleSectionExpand = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Select all columns
  const handleSelectAll = () => {
    const allSectionsSelected = Object.keys(selectedSections).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    
    setSelectedSections(allSectionsSelected);
    
    setSelectedColumns(prev => {
      const allColumnsSelected = {};
      Object.keys(allColumns).forEach(section => {
        allColumns[section].forEach(column => {
          allColumnsSelected[column.id] = true;
        });
      });
      return allColumnsSelected;
    });
  };

  // Select none (except required columns)
  const handleSelectNone = () => {
    const noSectionsSelected = Object.keys(selectedSections).reduce((acc, key) => {
      acc[key] = key === 'base'; // Only keep base section selected
      return acc;
    }, {});
    
    setSelectedSections(noSectionsSelected);
    
    setSelectedColumns(prev => {
      const onlyRequiredColumns = {};
      Object.keys(allColumns).forEach(section => {
        allColumns[section].forEach(column => {
          onlyRequiredColumns[column.id] = column.required;
        });
      });
      return onlyRequiredColumns;
    });
  };

  // Calculate total selected columns
  const getTotalSelectedColumns = () => {
    return Object.values(selectedColumns).filter(Boolean).length;
  };

  // Calculate selected columns in a section
  const getSelectedColumnCountInSection = (section) => {
    const visibleColumns = allColumns[section].filter(column => {
      // Handle North Prairie special case - hide NP Pipe columns if Fabrication is selected
      if (section === 'northPrairie') {
        const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
        return !(isNPPipeColumn && selectedSections.fabrication);
      }
      return true;
    });
    
    return visibleColumns.filter(column => selectedColumns[column.id]).length;
  };

  // Check if a section is partially selected (some but not all columns selected)
  const isSectionPartiallySelected = (section) => {
    const visibleColumns = allColumns[section].filter(column => {
      // Handle North Prairie special case - hide NP Pipe columns if Fabrication is selected
      if (section === 'northPrairie') {
        const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
        return !(isNPPipeColumn && selectedSections.fabrication);
      }
      return true;
    });
    
    const selectedCount = visibleColumns.filter(column => selectedColumns[column.id]).length;
    return selectedCount > 0 && selectedCount < visibleColumns.length;
  };

  // Check if a section is fully selected
  const isSectionFullySelected = (section) => {
    const visibleColumns = allColumns[section].filter(column => {
      // Handle North Prairie special case - hide NP Pipe columns if Fabrication is selected
      if (section === 'northPrairie') {
        const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
        return !(isNPPipeColumn && selectedSections.fabrication);
      }
      return true;
    });
    
    const selectedCount = visibleColumns.filter(column => selectedColumns[column.id]).length;
    return selectedCount === visibleColumns.length && visibleColumns.length > 0;
  };

  // Handle export
  const handleExport = () => {
    // Convert to the format expected by the original export function
    const sectionSelections = {
      base: selectedSections.base,
      engineering: selectedSections.engineering,
      fabrication: selectedSections.fabrication,
      controlPanel: selectedSections.controlPanel,
      pumps: selectedSections.pumps,
      assembly: selectedSections.assembly,
      test: selectedSections.test,
      northPrairie: selectedSections.northPrairie,
      shipping: selectedSections.shipping,
      portalDetails: selectedSections.portalDetails,
      details: selectedSections.details,
      // Add column-specific selections
      columns: selectedColumns,
      // Add format selection for export mode
      format: mode === 'export' ? exportFormat : 'print'
    };
    
    onExport(sectionSelections);
    onHide();
  };

  const getModalTitle = () => {
    return 'Export / Print - Select Format & Columns';
  };

  const getActionButtonText = () => {
    switch(exportFormat) {
      case 'pdf':
        return 'Export to PDF';
      case 'print':
        return 'Print Preview';
      case 'excel':
      default:
        return 'Export to Excel';
    }
  };

  return (
    <Modal className="job-schedule-modal" show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Format Selection */}
        <div className="mb-4">
          <h6>Select Format:</h6>
          <ButtonGroup className="w-100">
            <Button 
              variant={exportFormat === 'excel' ? 'primary' : 'outline-primary'}
              onClick={() => setExportFormat('excel')}
            >
              <i className="bi bi-file-earmark-excel me-2"></i>
              Excel (.xlsx)
            </Button>
            {/* Disable PDF option for now */}
            <Button 
              variant="outline-secondary"
              disabled
              title="PDF export is temporarily unavailable"
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              PDF (Coming Soon)
            </Button>
            <Button 
              variant={exportFormat === 'print' ? 'primary' : 'outline-primary'}
              onClick={() => setExportFormat('print')}
            >
              <i className="bi bi-printer me-2"></i>
              Print
            </Button>
          </ButtonGroup>
          <div className="mt-2">
            <small className="text-muted">
              {exportFormat === 'excel' 
                ? 'Excel format provides full data flexibility and custom formatting options.'
                : exportFormat === 'pdf'
                ? 'PDF format maintains visual formatting and is optimized for tabloid (11x17) printing.'
                : 'Print format opens a print preview optimized for tabloid (11x17) paper.'
              }
            </small>
          </div>
        </div>

        <p>Select specific columns to include in the {exportFormat === 'print' ? 'print' : exportFormat.toUpperCase()}:</p>
        
        <div className="d-flex justify-content-between mb-3">
          <Button variant="outline-primary" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleSelectNone}>
            Select None
          </Button>
        </div>
        
        <Accordion className="mb-3">
          {/* Base Information Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-base"
                  checked={selectedSections.base}
                  onChange={() => handleSectionToggle('base')}
                  disabled={true} // Base section is always enabled
                  label={`Base Information (${getSelectedColumnCountInSection('base')}/${allColumns.base.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('base')}
                aria-expanded={expandedSections.base}
              >
                <i className={`bi bi-chevron-${expandedSections.base ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.base && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.base.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                        disabled={column.required} // Required columns can't be unchecked
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Engineering Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-engineering"
                  checked={isSectionFullySelected('engineering')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('engineering');
                  }}
                  onChange={() => handleSectionToggle('engineering')}
                  label={`Engineering (${getSelectedColumnCountInSection('engineering')}/${allColumns.engineering.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('engineering')}
                aria-expanded={expandedSections.engineering}
              >
                <i className={`bi bi-chevron-${expandedSections.engineering ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.engineering && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.engineering.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>          {/* Add other sections following the same pattern... */}

          {/* Control Panel Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-controlPanel"
                  checked={isSectionFullySelected('controlPanel')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('controlPanel');
                  }}
                  onChange={() => handleSectionToggle('controlPanel')}
                  label={`Control Panel (${getSelectedColumnCountInSection('controlPanel')}/${allColumns.controlPanel.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('controlPanel')}
                aria-expanded={expandedSections.controlPanel}
              >
                <i className={`bi bi-chevron-${expandedSections.controlPanel ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.controlPanel && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.controlPanel.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Pumps Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-pumps"
                  checked={isSectionFullySelected('pumps')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('pumps');
                  }}
                  onChange={() => handleSectionToggle('pumps')}
                  label={`Pumps (${getSelectedColumnCountInSection('pumps')}/${allColumns.pumps.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('pumps')}
                aria-expanded={expandedSections.pumps}
              >
                <i className={`bi bi-chevron-${expandedSections.pumps ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.pumps && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.pumps.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Assembly Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-assembly"
                  checked={isSectionFullySelected('assembly')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('assembly');
                  }}
                  onChange={() => handleSectionToggle('assembly')}
                  label={`Assembly (${getSelectedColumnCountInSection('assembly')}/${allColumns.assembly.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('assembly')}
                aria-expanded={expandedSections.assembly}
              >
                <i className={`bi bi-chevron-${expandedSections.assembly ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.assembly && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.assembly.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Test Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-test"
                  checked={isSectionFullySelected('test')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('test');
                  }}
                  onChange={() => handleSectionToggle('test')}
                  label={`Test (${getSelectedColumnCountInSection('test')}/${allColumns.test.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('test')}
                aria-expanded={expandedSections.test}
              >
                <i className={`bi bi-chevron-${expandedSections.test ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.test && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.test.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* North Prairie Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-northPrairie"
                  checked={isSectionFullySelected('northPrairie')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('northPrairie');
                  }}
                  onChange={() => handleSectionToggle('northPrairie')}
                  label={`North Prairie (${getSelectedColumnCountInSection('northPrairie')}/${allColumns.northPrairie.filter(column => {
                    const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
                    return !(isNPPipeColumn && selectedSections.fabrication);
                  }).length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('northPrairie')}
                aria-expanded={expandedSections.northPrairie}
              >
                <i className={`bi bi-chevron-${expandedSections.northPrairie ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.northPrairie && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.northPrairie.filter(column => {
                    // Hide NP Pipe columns if Fabrication is selected
                    const isNPPipeColumn = column.id === 'npPipeFabDue' || column.id === 'npPipeFabDone';
                    return !(isNPPipeColumn && selectedSections.fabrication);
                  }).map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Portal Details Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-portalDetails"
                  checked={isSectionFullySelected('portalDetails')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('portalDetails');
                  }}
                  onChange={() => handleSectionToggle('portalDetails')}
                  label={`Portal Details (${getSelectedColumnCountInSection('portalDetails')}/${allColumns.portalDetails.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('portalDetails')}
                aria-expanded={expandedSections.portalDetails}
              >
                <i className={`bi bi-chevron-${expandedSections.portalDetails ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.portalDetails && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.portalDetails.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Additional Details Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-details"
                  checked={isSectionFullySelected('details')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('details');
                  }}
                  onChange={() => handleSectionToggle('details')}
                  label={`Additional Details (${getSelectedColumnCountInSection('details')}/${allColumns.details.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('details')}
                aria-expanded={expandedSections.details}
              >
                <i className={`bi bi-chevron-${expandedSections.details ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.details && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.details.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Fabrication Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-fabrication"
                  checked={isSectionFullySelected('fabrication')}
                  ref={(input) => {
                    if (input) input.indeterminate = isSectionPartiallySelected('fabrication');
                  }}
                  onChange={() => handleSectionToggle('fabrication')}
                  label={`Fabrication (${getSelectedColumnCountInSection('fabrication')}/${allColumns.fabrication.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('fabrication')}
                aria-expanded={expandedSections.fabrication}
              >
                <i className={`bi bi-chevron-${expandedSections.fabrication ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.fabrication && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.fabrication.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* Shipping Section */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <Form.Check
                  inline
                  type="checkbox"
                  id="select-section-shipping"
                  checked={selectedSections.shipping}
                  onChange={() => handleSectionToggle('shipping')}
                  label={`Shipping (${getSelectedColumnCountInSection('shipping')}/${allColumns.shipping.length})`}
                />
              </div>
              <Button 
                variant="link" 
                onClick={() => handleSectionExpand('shipping')}
                aria-expanded={expandedSections.shipping}
              >
                <i className={`bi bi-chevron-${expandedSections.shipping ? 'up' : 'down'}`}></i>
              </Button>
            </Card.Header>
            {expandedSections.shipping && (
              <Card.Body>
                <div className="d-flex flex-wrap">
                  {allColumns.shipping.map(column => (
                    <div key={column.id} style={{ width: '50%', padding: '0.25rem' }}>
                      <Form.Check
                        type="checkbox"
                        id={`column-${column.id}`}
                        label={column.name}
                        checked={selectedColumns[column.id] || false}
                        onChange={() => handleColumnToggle(column.id)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            )}
          </Card>
        </Accordion>

        <div className="mt-3 text-muted">
          Total columns selected: {getTotalSelectedColumns()}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleExport}>
          {getActionButtonText()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EnhancedExportModal;
